import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Message from "./Message";
import { useGetShowcasesQuery } from "../slices/showcasesApiSlice";

const SHOWCASE_QUERY_PARAMS = {};

const ProductCarousel4 = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { userInfo } = useSelector((state) => state.auth);
  const { data, isLoading, error } = useGetShowcasesQuery(SHOWCASE_QUERY_PARAMS);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleNavigateLinkChange = (navigateLink) => {
    if (navigateLink) navigate(`${navigateLink}`);
  };

  useEffect(() => {
    if (!data) return;

    const showcasesToShow = data.filter(
      (showcase) => showcase.present === "presentFour",
    );
    if (showcasesToShow.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((current) =>
        current === showcasesToShow.length - 1 ? 0 : current + 1,
      );
    }, 4000); // 4 seconds interval

    return () => clearInterval(interval);
  }, [data]);

  if (isLoading)
    return <div className="animate-pulse w-full h-full bg-slate-200"></div>;
  if (error)
    return (
      <Message variant="danger">{error?.data?.message || error.error}</Message>
    );

  // Filter for presentFour and sort by displayOrder
  const showcasesToShow = data
    ? data
        .filter((showcase) => showcase.present === "presentFour")
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    : [];

  if (showcasesToShow.length === 0)
    return <Message variant="info">No showcases available.</Message>;

  const nextSlide = () => {
    setActiveIndex((current) =>
      current === showcasesToShow.length - 1 ? 0 : current + 1,
    );
  };

  const prevSlide = () => {
    setActiveIndex((current) =>
      current === 0 ? showcasesToShow.length - 1 : current - 1,
    );
  };

  return (
    <div className="relative w-full h-full m-0 p-0 overflow-hidden group bg-slate-100">
      <div
        className="flex w-full h-full transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {showcasesToShow.map((showcase, index) => (
          <div
            key={showcase._id || index}
            className="w-full h-full flex-shrink-0 relative"
            onClick={() => handleNavigateLinkChange(showcase.navigateLink)}
            style={{ cursor: showcase.navigateLink ? "pointer" : "default" }}
          >
            <img
              src={showcase.image}
              alt={showcase.name || "Showcase"}
              className="w-full h-full block object-cover"
            />
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      {showcasesToShow.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/80 hover:bg-white text-slate-800 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-all focus:outline-none"
          >
            <FaChevronLeft className="pr-1 text-sm md:text-base" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/80 hover:bg-white text-slate-800 flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-all focus:outline-none"
          >
            <FaChevronRight className="pl-1 text-sm md:text-base" />
          </button>

          {/* Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {showcasesToShow.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(idx);
                }}
                className={`w-2 h-2 rounded-full transition-all shadow-sm ${
                  activeIndex === idx
                    ? "bg-white w-6"
                    : "bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductCarousel4;
